import React, { useState, useEffect, useMemo } from 'react';

// Nombre de la clave que usaremos en el localStorage
const STORAGE_KEY = 'adminMedicosData';

// Estructura de datos inicial si no hay nada en localStorage
const initialMedicosData = [
    { id: 1, nombre: 'Dr. Lisset', especialidad: 'Cardiología', consultorio_id: 101, correo: 'lisset@clinic.com' },
    { id: 2, nombre: 'Dr. Javier', especialidad: 'Pediatría', consultorio_id: 205, correo: 'javier@clinic.com' }
];

const initialMedico = {
    id: null, 
    nombre: '', 
    especialidad: '', 
    consultorio_id: '',
    correo: '',         
    password: ''       
};

export default function DashboardView() {
    // 1. Cargar datos del localStorage o usar los iniciales
    const [medicos, setMedicos] = useState(() => {
        try {
            const storedData = localStorage.getItem(STORAGE_KEY);
            return storedData ? JSON.parse(storedData) : initialMedicosData;
        } catch (error) {
            console.error("Error cargando datos de localStorage:", error);
            return initialMedicosData;
        }
    });

    // 2. Usar useEffect para guardar los datos cada vez que 'medicos' cambie
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(medicos));
        } catch (error) {
            console.error("Error guardando datos en localStorage:", error);
        }
    }, [medicos]); // Se ejecuta cada vez que la lista de médicos cambia

    // --- Nuevo Estado para la Búsqueda ---
    const [searchQuery, setSearchQuery] = useState('');

    // Estados para la Interfaz
    const [message, setMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [currentMedico, setCurrentMedico] = useState(initialMedico);
    const [isEditing, setIsEditing] = useState(false);

    // --- Funciones de Utilidad y Notificación ---
    const showMessage = (msg) => {
        setMessage(msg);
        setTimeout(() => setMessage(''), 3000);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsEditing(false);
        setCurrentMedico(initialMedico);
    };

    // --- Lógica del CRUD ---

    // CREAR/EDITAR
    const handleCreate = () => {
        setIsEditing(false);
        setCurrentMedico(initialMedico);
        setIsModalOpen(true);
    };

    const handleEdit = (medico) => {
        setIsEditing(true);
        // Usamos toString() para el consultorio_id en el modal de edición para evitar errores de tipo si el input es de tipo text
        setCurrentMedico({ ...medico, consultorio_id: String(medico.consultorio_id), password: '' }); 
        setIsModalOpen(true);
    };

    // ELIMINAR (Abre la modal de confirmación)
    const handleDelete = (medico) => {
        setCurrentMedico(medico);
        setIsDeleteModalOpen(true);
    };

    // Confirmación de ELIMINAR
    const confirmDelete = () => {
        setMedicos(medicos.filter(m => m.id !== currentMedico.id));
        showMessage(`[ELIMINADO] Médico ${currentMedico.nombre} eliminado permanentemente.`);
        setIsDeleteModalOpen(false);
        setCurrentMedico(initialMedico);
    };

    // Manejar cambios en el formulario
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentMedico({ ...currentMedico, [name]: value });
    };

    // Guardar el formulario (CREATE o UPDATE)
    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Asegurar que consultorio_id sea un número entero
        const consultorioIdParsed = parseInt(currentMedico.consultorio_id, 10);
        if (isNaN(consultorioIdParsed)) {
             showMessage("[ERROR] El ID del Consultorio debe ser un número.", true);
             return;
        }

        if (isEditing) {
            // Lógica de Edición (UPDATE)
            const updatedMedico = { 
                ...currentMedico,
                consultorio_id: consultorioIdParsed
            };
            if (updatedMedico.password === '') {
                 delete updatedMedico.password;
            } else {
                 // Aquí se podría añadir lógica real de hashing de contraseña
            }

            setMedicos(medicos.map(m => m.id === currentMedico.id 
                ? { ...m, ...updatedMedico } 
                : m)
            );
            showMessage(`[ACTUALIZADO] Médico ${currentMedico.nombre} modificado con éxito.`);
        } else {
            // Lógica de Creación (CREATE)
            const newId = medicos.length > 0 ? Math.max(...medicos.map(m => m.id)) + 1 : 1;
            
            setMedicos([...medicos, { 
                ...currentMedico, 
                id: newId, 
                consultorio_id: consultorioIdParsed // Usar el ID parseado
            }]);
            showMessage(`[CREADO] Médico ${currentMedico.nombre} agregado con éxito.`);
        }

        closeModal();
    };

    // --- Lógica de Filtrado de la Tabla (CRÍTICO) ---
    const filteredMedicos = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) {
            return medicos;
        }

        return medicos.filter(medico => 
            // Búsqueda en varios campos: nombre, especialidad, correo y consultorio
            medico.nombre.toLowerCase().includes(query) ||
            medico.especialidad.toLowerCase().includes(query) ||
            medico.correo.toLowerCase().includes(query) ||
            String(medico.consultorio_id).includes(query) // Convertimos a string para buscar
        );
    }, [medicos, searchQuery]);


    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto bg-white min-h-screen shadow-lg rounded-xl">

            {/* Notificación Visual Flotante */}
            {message && (
                <div className="fixed top-0 left-1/2 transform -translate-x-1/2 mt-4 z-50">
                    <div className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg shadow-xl animate-pulse transition duration-500">
                        {message}
                    </div>
                </div>
            )}

            <h1 className="text-3xl font-extrabold text-indigo-700 mb-6 border-b pb-2">Panel de Administración Médica</h1>

            {/* Controles: Botón Crear y Barra de Búsqueda */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
                
                {/* Botón para crear */}
                <button
                    onClick={handleCreate} 
                    className="flex items-center w-full md:w-auto justify-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 transition duration-150 transform hover:scale-[1.02]"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Agregar Nuevo Médico
                </button>
                
                {/* Barra de Búsqueda */}
                <div className="relative w-full md:max-w-md">
                    <input
                        type="text"
                        placeholder="Buscar por nombre, especialidad o correo..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full py-3 pl-10 pr-4 border border-gray-300 rounded-xl shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 transition duration-150"
                    />
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                </div>
            </div>

            <h2 className="text-xl font-semibold text-gray-700 mb-4">Gestión de Médicos ({filteredMedicos.length})</h2>

            {/* Tabla de Médicos */}
            <div className="overflow-x-auto bg-white shadow-lg rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-indigo-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">ID</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Nombre</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Especialidad</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Consultorio ID</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Correo</th>
                            <th className="px-6 py-3 text-right text-xs font-semibold text-indigo-700 uppercase tracking-wider">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                        {filteredMedicos.map((medico) => (
                            <tr key={medico.id} className="hover:bg-indigo-50 transition duration-100">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{medico.id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">{medico.nombre}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{medico.especialidad}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{medico.consultorio_id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{medico.correo}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        onClick={() => handleEdit(medico)} 
                                        className="text-indigo-600 hover:text-indigo-800 mr-4 p-1 rounded-md hover:bg-indigo-100 transition duration-150"
                                    >
                                        Editar
                                    </button>
                                    <button
                                        onClick={() => handleDelete(medico)} 
                                        className="text-red-600 hover:text-red-800 p-1 rounded-md hover:bg-red-100 transition duration-150"
                                    >
                                        Eliminar
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filteredMedicos.length === 0 && (
                            <tr>
                                <td colSpan="6" className="px-6 py-8 text-center text-gray-500 italic">
                                    No se encontraron médicos que coincidan con la búsqueda.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* MODAL de CREAR/EDITAR */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-8">
                        <h3 className="text-2xl font-bold mb-6 text-indigo-700 border-b pb-2">
                            {isEditing ? '🖊️ Editar Médico' : '➕ Agregar Nuevo Médico'}
                        </h3>
                        
                        <form onSubmit={handleSubmit}>
                            
                            {/* Grupo de datos personales/profesionales */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Campo Nombre */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre:</label>
                                    <input
                                        type="text"
                                        name="nombre"
                                        required
                                        value={currentMedico.nombre}
                                        onChange={handleFormChange}
                                        className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nombre completo"
                                    />
                                </div>

                                {/* Campo Especialidad */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Especialidad:</label>
                                    <input
                                        type="text"
                                        name="especialidad"
                                        required
                                        value={currentMedico.especialidad}
                                        onChange={handleFormChange}
                                        className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: Pediatría, Dermatología"
                                    />
                                </div>
                                
                                {/* Campo Consultorio ID */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Consultorio ID:</label>
                                    <input
                                        type="number"
                                        name="consultorio_id"
                                        required
                                        value={currentMedico.consultorio_id}
                                        onChange={handleFormChange}
                                        className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: 101"
                                    />
                                </div>
                                
                                {/* Campo Correo */}
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-semibold mb-2">Correo Electrónico:</label>
                                    <input
                                        type="email"
                                        name="correo"
                                        required
                                        value={currentMedico.correo}
                                        onChange={handleFormChange}
                                        className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="correo@dominio.com"
                                    />
                                </div>
                            </div>

                            {/* Campo Contraseña */}
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Contraseña {isEditing ? '(Dejar vacío para no cambiar)' : '*'}:</label>
                                <input
                                    type="password"
                                    name="password"
                                    required={!isEditing}
                                    value={currentMedico.password}
                                    onChange={handleFormChange}
                                    className="shadow-sm border-gray-300 border rounded-lg w-full py-3 px-4 text-gray-700 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder={isEditing ? '••••••••' : 'Contraseña inicial'}
                                />
                            </div>

                            {/* Botones de acción */}
                            <div className="flex justify-end space-x-4 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-xl hover:bg-gray-400 transition duration-150 shadow-md"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="bg-indigo-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:bg-indigo-700 transition duration-150"
                                >
                                    {isEditing ? 'Guardar Cambios' : 'Guardar Médico'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL de CONFIRMACIÓN de ELIMINACIÓN */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
                         <div className="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.332 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h3 className="text-xl font-bold leading-6 text-gray-900 mt-4">Confirmar Eliminación</h3>
                            <p className="text-sm text-gray-500 mt-2">
                                ¿Estás seguro de que quieres eliminar permanentemente al médico <strong>{currentMedico.nombre}</strong> (ID: {currentMedico.id})? Esta acción no se puede deshacer.
                            </p>
                        </div>
                        
                        <div className="mt-5 sm:mt-6 flex justify-between space-x-4">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 transition duration-150"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={confirmDelete}
                                className="inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 transition duration-150"
                            >
                                Sí, Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}